import { Component } from 'react'
import * as cn from 'classnames'
import * as debounce from 'lodash/debounce'
import * as styles from 'styles/molecules/tables/Paginator.css'

import PaginatorArrow from 'components/atoms/tables/PaginatorArrow'

export type PaginatorStateProps = {
  recordsCount: number
  pageSize: number
  page: number
  className?: string
  pageClassName?: string
}

type PaginatorDispatchProps = {
  onPageChange: (id: number) => void
}

export type PaginatorProps = PaginatorStateProps & PaginatorDispatchProps

type PaginatorState = {
  pageCellsCount: number
}

/**
 * Overall amount of cells in paginator including ellipsises
 * @type {number}
 */
// this is knowingly bigger than any cell count we do want to show
// this is needed for an initial scenario when we start calculating cells count;
// at this moment a paginator should be as wide as it can
const DEFAULT_PAGE_CELLS_COUNT = 50
const LEFT_ELLIPSIS = -1
const RIGHT_ELLIPSIS = -2
const RESIZE_DEBOUNCE_TIME = 100

const DEFAULT_STATE: PaginatorState = {
  pageCellsCount: DEFAULT_PAGE_CELLS_COUNT,
}

class Paginator extends Component<PaginatorProps, PaginatorState> {

  state = DEFAULT_STATE
  private pagesNode: HTMLElement | null = null

  private get pagesCount () {
    const { recordsCount, pageSize } = this.props

    return Math.ceil(recordsCount / pageSize)
  }

  private get shouldStretch () {
    return this.state.pageCellsCount < this.pagesCount
  }

  render () {
    const { page, className, pageClassName, onPageChange } = this.props
    const isFirstPage = page === 1
    const lastPage = this.calculateLastPage()
    const isLastPage = page === lastPage

    // if there is only a single page
    if (lastPage === 1) {
      return null
    }

    const pageRange = this.createPageRange()

    const rootClassName = cn(styles.paginator, {
      [styles.stretching]: this.shouldStretch,
    }, className)

    return (
      <div className={ rootClassName }>
        <div className={ cn(styles.cell, { [styles.disabled]: isFirstPage }) }>
          <PaginatorArrow
            qaMarker='double-left'
            direction='left'
            kind='double'
            disabled={ isFirstPage }
            onClick={ () => onPageChange(1) }
          />
        </div>
        <div className={ cn(styles.cell, { [styles.disabled]: isFirstPage }) }>
          <PaginatorArrow
            qaMarker='single-left'
            direction='left'
            kind='single'
            disabled={ isFirstPage }
            onClick={ () => onPageChange(page - 1) }
          />
        </div>
        <ul
          ref={ (node) => { this.pagesNode = node } }
          className={ styles.pages }
        >
          {
            pageRange.map((pageNumber) => {
              const isCurrentPage = pageNumber === page
              const isEllipsis = pageNumber === LEFT_ELLIPSIS || pageNumber === RIGHT_ELLIPSIS
              const content = isEllipsis ? '…' : pageNumber
              const onClick = isEllipsis || isCurrentPage ? undefined : () => onPageChange(pageNumber)

              const pageWrapperClassName = cn(styles.page, {
                [styles.current]: isCurrentPage && !isEllipsis,
                [styles.ellipsis]: isEllipsis,
              }, pageClassName)

              return (
                <li
                  key={ pageNumber }
                  className={ pageWrapperClassName }
                  onClick={ onClick } 
                  data-qa-marker={ isEllipsis ? 'elipsis' : `page-${ pageNumber }` }
                >{ content }
                </li>
              )
            })
          }
        </ul>
        <div className={ cn(styles.cell, { [styles.disabled]: isLastPage }) }>
          <PaginatorArrow
            qaMarker='single-right'
            direction='right'
            kind='single'
            disabled={ isLastPage }
            onClick={ () => onPageChange(page + 1) }
          />
        </div>
        <div className={ cn(styles.cell, { [styles.disabled]: isLastPage }) }>
          <PaginatorArrow
            qaMarker='double-right'
            direction='right'
            kind='double'
            disabled={ isLastPage }
            onClick={ () => onPageChange(lastPage) }
          />
        </div>
      </div>
    )
  }

  componentDidMount () {
    this.calculateCellsCount()
    // TODO support `resize` in OuterEventTracker
    window.addEventListener('resize', this.onResize)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.onResize)
    this.onResize.cancel()
  }

  private calculateLastPage (): number {
    const { recordsCount, pageSize } = this.props

    return Math.ceil(recordsCount / pageSize)
  }

  private createPageRange (): number[] {
    const { page } = this.props
    const { pageCellsCount } = this.state

    const lastPage = this.calculateLastPage()

    let paginatorLength = lastPage < pageCellsCount ? lastPage : pageCellsCount
    const paginatorLengthHalf = Math.floor(paginatorLength / 2)
    paginatorLength = paginatorLengthHalf * 2 + 1

    let leftEdgeReachable = page - paginatorLengthHalf <= 1
    let rightEdgeReachable = page + paginatorLengthHalf >= lastPage
    let startPage = page - paginatorLengthHalf
    if (leftEdgeReachable) {
      startPage = 1
      if (startPage + lastPage - 1 <= pageCellsCount) {
        rightEdgeReachable = true
      }
    } else if (rightEdgeReachable) {
      startPage = lastPage - pageCellsCount + 1
      if (startPage <= 1) {
        leftEdgeReachable = true
        startPage = 1
      }
    }

    const end = startPage + Math.min(lastPage, pageCellsCount) - 1

    let i = startPage
    const pageRange: number[] = []
    while (i <= end) {
      if (i === startPage) {
        pageRange.push(leftEdgeReachable ? i : LEFT_ELLIPSIS)
      } else if (i === end) {
        pageRange.push(rightEdgeReachable ? lastPage : RIGHT_ELLIPSIS)
      } else {
        pageRange.push(i)
      }

      i++
    }

    return pageRange
  }

  private calculateCellsCount = () => {
    if (!this.pagesNode) {
      return
    }

    const rect = this.pagesNode.getBoundingClientRect()
    const pageCellWidth = parseInt(styles.pageCellSize, 10)

    const cellsCount = Math.max(Math.floor(rect.width / pageCellWidth), 0)

    this.setState({
      pageCellsCount: cellsCount,
    })
  }

  private onResize = debounce(
    () => {
      this.setState(() => DEFAULT_STATE, this.calculateCellsCount)
    },
    RESIZE_DEBOUNCE_TIME
  )
}

export default Paginator
